import { isAccountOwner } from '@/features/account-access/lib/roles'
import {
  AccountRole,
  ParadigmBindingState,
  type PublicAccount,
} from '@/types/generated/common/account'
import { TradeVenue } from '@/types/generated/common/types'
import { ParadigmAccountInfo, ParadigmBindingEvidence } from '@/types/generated/public_api'

/**
 * Venues an account can bind to a Paradigm desk on: the ones Paradigm settles on (DBT,
 * BYB). Mirrors the backend gate, which refuses every other venue.
 */
export function supportsParadigmBinding(venue: TradeVenue): boolean {
  return venue === TradeVenue.TRADE_VENUE_DERIBIT || venue === TradeVenue.TRADE_VENUE_BYBIT
}

/**
 * A Paradigm desk labels its own trading accounts with free-text nicknames, and the
 * binding records which nickname is this OrderX account. UNSPECIFIED is what every
 * pre-binding row and every older client decodes to — "no data", never "verified" — so
 * fold it (and any future state this client doesn't know) onto UNVERIFIED rather than
 * letting a zero read as fine. Same idiom `normalizeRfqNetwork` applies to `rfqNetwork`.
 */
export function normalizeParadigmBindingState(state: ParadigmBindingState): ParadigmBindingState {
  switch (state) {
    case ParadigmBindingState.PARADIGM_BINDING_STATE_VERIFIED:
    case ParadigmBindingState.PARADIGM_BINDING_STATE_MISMATCH:
      return state
    default:
      return ParadigmBindingState.PARADIGM_BINDING_STATE_UNVERIFIED
  }
}

/** Badge colour band for a binding state. UNSPECIFIED lands on amber, via the normalize. */
export type ParadigmBindingTone = 'verified' | 'unverified' | 'mismatch'

export function paradigmBindingTone(state: ParadigmBindingState): ParadigmBindingTone {
  switch (normalizeParadigmBindingState(state)) {
    case ParadigmBindingState.PARADIGM_BINDING_STATE_VERIFIED:
      return 'verified'
    case ParadigmBindingState.PARADIGM_BINDING_STATE_MISMATCH:
      return 'mismatch'
    default:
      return 'unverified'
  }
}

export type ParadigmBindingStateKey =
  | 'paradigmStateVerified'
  | 'paradigmStateUnverified'
  | 'paradigmStateMismatch'

export function paradigmBindingStateKey(state: ParadigmBindingState): ParadigmBindingStateKey {
  switch (paradigmBindingTone(state)) {
    case 'verified':
      return 'paradigmStateVerified'
    case 'mismatch':
      return 'paradigmStateMismatch'
    default:
      return 'paradigmStateUnverified'
  }
}

export type ParadigmEvidenceKey =
  | 'paradigmEvidenceProvenThis'
  | 'paradigmEvidenceProvenOther'
  | 'paradigmEvidenceNoHistory'
  | 'paradigmEvidenceUnknown'

/**
 * UNSPECIFIED evidence is "the server told us nothing", which is NOT the same claim as
 * NO_HISTORY ("we looked and this nickname has never traded") — an unknown gets its own
 * label rather than borrowing the stronger one. Both are equally unproven, so both leave
 * the candidate below a proven one in `sortParadigmAccounts`.
 */
export function paradigmEvidenceKey(evidence: ParadigmBindingEvidence): ParadigmEvidenceKey {
  switch (evidence) {
    case ParadigmBindingEvidence.PARADIGM_BINDING_EVIDENCE_PROVEN_THIS_ACCOUNT:
      return 'paradigmEvidenceProvenThis'
    case ParadigmBindingEvidence.PARADIGM_BINDING_EVIDENCE_PROVEN_OTHER_ACCOUNT:
      return 'paradigmEvidenceProvenOther'
    case ParadigmBindingEvidence.PARADIGM_BINDING_EVIDENCE_NO_HISTORY:
      return 'paradigmEvidenceNoHistory'
    default:
      return 'paradigmEvidenceUnknown'
  }
}

// Picker order, best pick first. PROVEN_OTHER_ACCOUNT sorts LAST, below even an unknown:
// linking it is the one choice guaranteed to land the account straight back in MISMATCH
// (the backend maps that evidence to exactly that state).
const EVIDENCE_RANK: ReadonlyMap<ParadigmBindingEvidence, number> = new Map([
  [ParadigmBindingEvidence.PARADIGM_BINDING_EVIDENCE_PROVEN_THIS_ACCOUNT, 0],
  [ParadigmBindingEvidence.PARADIGM_BINDING_EVIDENCE_NO_HISTORY, 1],
  [ParadigmBindingEvidence.PARADIGM_BINDING_EVIDENCE_UNSPECIFIED, 2],
  [ParadigmBindingEvidence.PARADIGM_BINDING_EVIDENCE_PROVEN_OTHER_ACCOUNT, 3],
])

const UNKNOWN_EVIDENCE_RANK = 2

function tradedAt(info: ParadigmAccountInfo): number {
  const ms = Number(info.lastTradedAtMs ?? '0')
  return Number.isFinite(ms) ? ms : 0
}

/**
 * Candidate nicknames for the picker, best-evidence first, then by how much of this
 * account's own history they account for. Returns a new array — the response's own
 * `accounts` is left alone.
 */
export function sortParadigmAccounts(accounts: ParadigmAccountInfo[]): ParadigmAccountInfo[] {
  return [...accounts].sort((a, b) => {
    const rank =
      (EVIDENCE_RANK.get(a.evidence) ?? UNKNOWN_EVIDENCE_RANK) -
      (EVIDENCE_RANK.get(b.evidence) ?? UNKNOWN_EVIDENCE_RANK)
    if (rank !== 0) return rank
    if (a.matchedTradeCount !== b.matchedTradeCount)
      return b.matchedTradeCount - a.matchedTradeCount
    if (a.paradigmTradeCount !== b.paradigmTradeCount) {
      return b.paradigmTradeCount - a.paradigmTradeCount
    }
    const traded = tradedAt(b) - tradedAt(a)
    if (traded !== 0) return traded
    // Names are the desk's own free text and are unique within it, so this is a stable
    // final tiebreak rather than leaving equal candidates in server order.
    return a.accountName.localeCompare(b.accountName)
  })
}

/** Which Paradigm controls an account row offers. */
export interface ParadigmRowActions {
  canConnect: boolean
  canChange: boolean
  canDisconnect: boolean
}

/**
 * Connect/Change mirror the wizard RPCs' Admin gate, which `isAccountOwner` matches
 * exactly — the backend's `AccessLevel::Admin` is satisfied by a held Admin OR Owner
 * permission, and that predicate covers OWNER plus the deprecated ADMIN role.
 *
 * Disconnect is stricter and needs its OWN predicate. The backend's
 * `AccessLevel::Owner` is `matches!(held, Permission::Owner)` — a held Admin does NOT
 * satisfy it (`shared/dynamodb/src/accounts.rs` asserts exactly that). Reusing
 * `isAccountOwner` here would show a legacy-ADMIN member a Disconnect button the
 * server always answers with "Owner access to this account is required".
 *
 * Hiding a control that would only ever be refused is not a substitute for the server's
 * checks — every refusal that can still happen (plan capability, bad credentials, a role
 * changed concurrently) surfaces verbatim.
 *
 * Disconnect is deliberately NOT plan-gated: a user whose plan lapsed must still be able
 * to unwind a binding they can no longer create.
 */
export function paradigmRowActions(
  account: Pick<PublicAccount, 'role' | 'paradigmBinding'>
): ParadigmRowActions {
  const canAdmin = isAccountOwner(account.role)
  const isStrictOwner = account.role === AccountRole.ACCOUNT_ROLE_OWNER
  const bound = !!account.paradigmBinding
  return {
    canConnect: canAdmin && !bound,
    canChange: canAdmin && bound,
    canDisconnect: isStrictOwner && bound,
  }
}

/**
 * The Paradigm nickname typed into the picker's free-text fallback, or undefined when
 * there is nothing to submit.
 *
 * Deliberately does NOT validate the name against `list_accounts`' response. That
 * listing only ever contains nicknames Paradigm has seen TRADES under, so a brand-new
 * subaccount can never appear in it — checking against it would lock out the exact case
 * this input exists for. The backend agrees: an unlisted nickname yields no match keys,
 * classifies NoHistory, and binds UNVERIFIED rather than being rejected.
 *
 * Only surrounding whitespace is stripped. Case and interior spacing are preserved
 * verbatim, because the binding is an exact match against the desk's own free-text
 * nickname — "deribit 2 sub" is not "Deribit 2 Sub".
 */
export function paradigmManualLinkName(raw: string): string | undefined {
  const name = raw.trim()
  return name === '' ? undefined : name
}
